/**
 * The source of truth for tier card content. The portal imports `plans` too
 * (@terramantle/marketing/content: the new-org wizard and the Billing tab), so an
 * edit here changes both. Keep this file free of imports so the portal can bundle it.
 */

export interface PricingPlan {
  name: string
  price: string
  cadence: string
  annualPrice?: string
  eyebrow: string
  summary: string
  featured?: boolean
  cta: string
  ctaHref: string
  modalKey: 'onboarding' | 'enterprise-quote' | null
  features: Array<{ included: boolean; label: string }>
}

export type ComparisonCell = boolean | string

export interface ComparisonRow {
  label: string
  description?: string
  values: [ComparisonCell, ComparisonCell, ComparisonCell, ComparisonCell]
}

export interface ComparisonGroup {
  group: string
  rows: ComparisonRow[]
}

export const plans: PricingPlan[] = [
  {
    name: 'Solo',
    price: 'Free',
    cadence: 'Cloud',
    eyebrow: 'For individuals',
    summary: 'A private registry, state backend and plan gate for one engineer. Scanning and keyless CI publishing on every push. Free forever, no card.',
    cta: 'Sign in',
    ctaHref: 'https://portal.terramantle.dev',
    modalKey: null,
    features: [
      { included: true, label: 'Module, provider & state registry' },
      { included: true, label: 'Plan gates for destroys & replacements' },
      { included: true, label: 'Scanning, KEV matching & policy reporting' },
      { included: true, label: 'Keyless CI publishing (GitHub/GitLab)' },
      { included: true, label: '1 user · 30-day state history' },
    ],
  },
  {
    name: 'Team',
    price: '£24',
    cadence: '/org/mo',
    annualPrice: '£264',
    eyebrow: 'For growing teams',
    summary: 'Bring the team on. Ten users included in one flat price per org, and £3 a month for each one after that. Sign in with SSO and get pinged the moment a module or provider drifts.',
    cta: 'Sign up today',
    ctaHref: 'https://portal.terramantle.dev',
    modalKey: 'onboarding',
    features: [
      { included: true, label: 'Everything in Solo' },
      { included: true, label: '10 users included' },
      { included: true, label: 'Single sign-on (OIDC)' },
      { included: true, label: 'Slack & Teams alerts' },
      { included: true, label: 'Unlimited module versions' },
      { included: true, label: '30-day audit · 90-day state history' },
    ],
  },
  {
    name: 'Business',
    price: '£149',
    cadence: '/org/mo',
    annualPrice: '£1,639',
    eyebrow: 'For larger teams',
    summary: 'Lock down your provider supply chain for the whole platform team, fifty users included. CVE and malware scanning, allow-list gates, patched provider builds, and policy that blocks bad modules before they ship.',
    featured: true,
    cta: 'Sign up today',
    ctaHref: 'https://portal.terramantle.dev',
    modalKey: 'onboarding',
    features: [
      { included: true, label: 'Everything in Team' },
      { included: true, label: '50 users included' },
      { included: true, label: 'Serve providers from your own registry' },
      { included: true, label: 'CVE & malware scanning with allow-list gates' },
      { included: true, label: 'CVE patched provider builds' },
      { included: true, label: 'OPA Policy Enforcement & kill switch' },
      { included: true, label: '90-day audit · 1-year state history' },
    ],
  },
  {
    name: 'Enterprise',
    price: 'From £249',
    cadence: '/org/mo',
    annualPrice: 'From £2,739',
    eyebrow: 'Self-hosted or cloud',
    summary: 'Run it your way: cloud, self-hosted, or fully air-gapped. Unlimited history, SCIM, audit streamed to your SIEM, and a named engineer on the other end.',
    cta: 'Get a quote',
    ctaHref: 'https://portal.terramantle.dev',
    modalKey: 'enterprise-quote',
    features: [
      { included: true, label: 'Everything in Business' },
      { included: true, label: 'As many users as you need, with SCIM provisioning' },
      { included: true, label: 'Self-hosted or air-gapped' },
      { included: true, label: 'Audit streaming to your SIEM' },
      { included: true, label: 'Unlimited audit & state history' },
      { included: true, label: 'Priority SLA and support' },
    ],
  },
]

export interface IncludedGroup {
  group: string
  items: Array<{ label: string; description: string }>
}

/**
 * What every tier gets, including the free one. Shown as a grouped strip above
 * the comparison matrix so the matrix itself only carries rows that differ.
 */
export const everyTierIncludes: IncludedGroup[] = [
  {
    group: 'Registry & state',
    items: [
      { label: 'Private module, provider & state registry', description: 'Terraform and OpenTofu HTTP state backend with locking. No runners: your CI keeps running terraform.' },
      { label: 'Provider inventory from lock files', description: 'Every workspace\'s lock file is ingested, so you know exactly which provider versions are in use and where.' },
      { label: 'State operations', description: 'Delegated force-unlock, restore an earlier version, remove resources from state, and a run timeline per workspace.' },
    ],
  },
  {
    group: 'Publish & plan',
    items: [
      { label: 'Keyless CI publishing', description: 'GitHub and GitLab OIDC trust. No long-lived credentials in your pipelines.' },
      { label: 'Plan gates', description: 'Blast-radius gates on destroys and forced replacements, correlated across workspaces so a cascade is caught before it starts, and an attestation that what was applied is what was reviewed.' },
      { label: 'Redaction by design', description: 'Plan values, before and after, never reach storage. Secrets found in state are reported, not kept.' },
    ],
  },
  {
    group: 'Scanning & signals',
    items: [
      { label: 'Scanning on every push', description: 'Misconfiguration, vulnerability and secret detection on modules and state, with CISA KEV matching.' },
      { label: 'Community Repository Health Signals', description: 'Build policy gates around Open Source metrics like maintainer activity, GitHub stars, releases and abandonment stats for the community providers you depend on.' },
      { label: 'Passive drift detection', description: 'A scheduled refresh-only plan from your CI, pushed to Terramantle, so drift is found and alerted on without anyone running an apply.' },
    ],
  },
  {
    group: 'Policy',
    items: [
      { label: 'Policy editor', description: 'A fully featured in-browser Rego editor that autocompletes every input field from the live schema, so scan findings, licences and repository health are a keystroke away. Run a policy as advisory until you are ready to enforce it.' },
      { label: 'Policy template library', description: 'Over fifty ready-made policies for modules, state, plans and providers: block actively exploited CVEs, quarantine brand-new releases, refuse abandoned sources, protect stateful resources from a destroy. Apply one, or the curated baseline, in a click.' },
      { label: 'Policy reporting & dependency graph', description: 'OPA results per module version, role-based access control, and deprecation notices that reach the engineer in their terraform output, not just a dashboard.' },
    ],
  },
  {
    group: 'Tooling & insight',
    items: [
      { label: 'Federated Single Sign-On', description: 'Microsoft, Google, GitHub and GitLab Federated Access works by default. No sign up forms to forget or misplace - sign in takes you straight into the app.' },
      { label: 'The Terramantle CLI', description: 'Scaffold a mono or poly repo with GitHub or GitLab CI, publish modules with conventional-commit versioning, push lock files with a fail-on-at-risk flag, and promote, roll back or unlock state.' },
      { label: 'Analytics dashboard', description: 'Downloads and applies, top modules and actors, the riskiest modules and workspaces, slow applies, your infrastructure footprint, and continuous-patching exposure: vulnerable superseded versions still being downloaded.' },
      { label: 'Break-glass overrides with an audit trail', description: 'Manually block a version, lift a block, override a policy refusal for one version, or set a serve override per module or version. Every override is recorded against who did it.' },
    ],
  },
]

// Columns: [Solo, Team, Business, Enterprise]. Only rows that differ between tiers belong here.
export const comparisonGroups: ComparisonGroup[] = [
  {
    group: 'Limits',
    rows: [
      { label: 'Users included', description: 'Counted into the flat org price. On Team and Business each billable user past this is £3 a month; a member who was idle all month is not billable. Bot and CI identities never count.', values: ['1', '10', '50', 'Custom'] },
      { label: 'Bot tokens', description: 'Tokens for automated workflows and self-hosted CI. Rotate a secret in place without revoking the token.', values: ['1', '3', '10', 'Custom'] },
      { label: 'Module versions retained', description: 'Oldest versions are removed once the limit is reached.', values: ['3', '10', 'Configurable', 'Configurable'] },
      { label: 'State File revisions retained', description: 'How far back you can restore a state version.', values: ['3', '10', 'Configurable', 'Configurable'] },
      { label: 'Audit log retention', values: ['7 days', '14 days', '90 days', 'Unlimited'] },
    ],
  },
  {
    group: 'Collaboration',
    rows: [
      { label: 'Slack & Teams alerts', description: 'Drift, deprecations and new security findings.', values: [false, true, true, true] },
      { label: 'Email domain claim', description: 'Claim your domain so colleagues who sign in with it land in your org automatically.', values: [false, true, true, true] },
      { label: 'Single Sign-On (OIDC)', description: 'More granular and explicit access controls managed from your Identity Provider', values: [false, false, true, true] },
      { label: 'SCIM Provisioning', values: [false, false, true, true] },
    ],
  },
  {
    group: 'Provider Provenance & Supply Chain',
    rows: [
      { label: 'Provider Digest', description: 'Choose how often you hear about new versions of the providers you depend on.', values: [false, true, true, true] },
      { label: 'Terramantle Built Providers', description: 'Community providers built and served to OpenTofu and Terraform, with allow lists and RBAC.', values: [false, false, true, true] },
      { label: 'CVE & Anti-Malware', description: 'Provider source and dependencies scanned against multiple CVE sources and Anti-Malware engines.', values: [false, false, true, true] },
      { label: 'CVE Patched Builds', description: 'A remediated revision built, signed and served, with an Open Source diff against upstream provider source code.', values: [false, false, true, true] },
      { label: 'Provider Allow-list gates', description: 'Block, not just flag, providers your teams may not consume.', values: [false, false, true, true] },
      { label: 'Intent Analysis', description: 'A finely tuned AI model inspects for common supply chain vulnerabilities like exfiltration, obfuscation and backdoors before any provider binary is built.', values: [false, false, true, true] },
      { label: 'Gate-backed Signing & SBOM', description: 'A signature that asserts SAST, intent analysis, AV and dependency audit all passed.', values: [false, false, true, true] },
    ],
  },
  {
    group: 'Policy & Control',
    rows: [
      { label: 'Blast Radius Protection', description: 'Stop entire Terraform stacks from being accidentally destroyed', values: [false, false, true, true] },
      { label: 'Policy Enforcement', description: 'A well documented schema provides data and metrics from vulnerability & misconfiguration scans, community metrics like Open Source licenses, GitHub stars, commits, releases for a modern approach on Open Source compliance.', values: [false, false, true, true] },
      { label: 'Policy checks at publish time', description: 'Real-time instant feedback for your CI instead of a finding later.', values: [false, false, true, true] },
      { label: 'Consumption kill switch', description: 'One switch stops every download for the org.', values: [false, false, true, true] },
      { label: 'Public endpoint discovery', description: 'Find and alert on publicly reachable endpoints discovered from your state files.', values: [false, false, true, true] },
      { label: 'null_resource exploitation scanning', values: [false, false, false, true] },
    ],
  },
  {
    group: 'Compliance',
    rows: [
      { label: 'Policy decision ledger', description: 'Every refusal and override recorded, with per-policy coverage and block history.', values: [false, false, true, true] },
      { label: 'Audit streaming', description: 'Stream audit logs to your SIEM.', values: [false, false, true, true] },
      { label: 'Self-hosted & Air-gapped Deployments', description: 'The same platform on your Kubernetes, inside your network, with no outbound dependency. Mirror what you need in, serve it from inside.', values: [false, false, 'Contact Us', true] },
      { label: 'Data Diode Transfer Integrity', description: 'Terramantle provides signed, hash-chained transfer bundles to survive one-way links, so the air-gapped side verifies the full chain of trust from upstream to serve.', values: [false, false, 'Contact Us', true] },
    ],
  },
]
